/**
 * ComponenteUploadSindicaisJurisprudencia
 *
 * CONTEXTO DE NEGÓCIO:
 * Este componente provê duas áreas de upload opcionais para documentos que enriquecem
 * as análises de petições: Convenções Sindicais e Jurisprudência.
 * Usuários podem enviar múltiplos arquivos em cada área. Cada área realiza uploads
 * assíncronos individuais e associa os documentos ao processo via `processoId`.
 *
 * INTEGRAÇÃO:
 * - Usa `ComponenteUploadDocumentos` (padrão de upload assíncrono, polling)
 * - Passa `categoriaDocumento` como 'convencoes_sindicais' ou 'jurisprudencia'
 * - Passa `processoId` (quando disponível) para que o backend associe os documentos
 *
 * PROPRIEDADES:
 * - processoId?: string -> id do processo para associar uploads
 * - aoFinalizarUploads: callback com mapa { categoria: ids[] } quando todos uploads concluírem
 * - aoOcorrerErro: callback de erro global
 */

import { useState } from 'react';
import { ComponenteUploadDocumentos } from '../upload/ComponenteUploadDocumentos';

interface PropsComponenteUploadSindicaisJurisprudencia {
  /** ID do processo para associar os uploads (opcional) */
  processoId?: string;

  /**
   * Callback chamado quando uploads de alguma das categorias forem concluídos com sucesso
   * Retorna um objeto com chaves 'convencoes_sindicais' e/ou 'jurisprudencia' contendo arrays de ids
   */
  aoFinalizarUploads?: (idsPorCategoria: { convencoes_sindicais?: string[]; jurisprudencia?: string[] }) => void;

  /** Callback chamado se ocorrer erro */
  aoOcorrerErro?: (mensagemErro: string) => void;
}

export function ComponenteUploadSindicaisJurisprudencia({
  processoId,
  aoFinalizarUploads,
  aoOcorrerErro,
}: PropsComponenteUploadSindicaisJurisprudencia): JSX.Element {
  // Estado local para armazenar ids retornados por cada área
  const [idsConvencoes, setIdsConvencoes] = useState<string[]>([]);
  const [idsJurisprudencia, setIdsJurisprudencia] = useState<string[]>([]);

  return (
    <div className="space-y-6">
      {/* Area: Convenções Sindicais */}
      <div className="rounded-md border border-gray-200 bg-white p-4">
        <h3 className="text-lg font-semibold">Convenções Sindicais (opcional)</h3>
        <p className="text-sm text-gray-600">Envie convenções coletivas ou ACT/CCT relevantes para o processo.</p>

        <div className="mt-3">
          <ComponenteUploadDocumentos
            permitirMultiplosArquivos={true}
            categoriaDocumento="convencoes_sindicais"
            processoId={processoId}
            aoFinalizarUploadComSucesso={(ids) => {
              console.log('[ComponenteUploadSindicaisJurisprudencia] Convenções enviadas:', ids);
              setIdsConvencoes((prev) => {
                const combinado = [...prev, ...ids];
                aoFinalizarUploads?.({ convencoes_sindicais: combinado, jurisprudencia: idsJurisprudencia });
                return combinado;
              });
            }}
            aoOcorrerErroNoUpload={(mensagemErro) => {
              console.error('[ComponenteUploadSindicaisJurisprudencia] Erro (convencoes):', mensagemErro);
              aoOcorrerErro?.(mensagemErro);
            }}
          />
        </div>
      </div>

      {/* Area: Jurisprudência */}
      <div className="rounded-md border border-gray-200 bg-white p-4">
        <h3 className="text-lg font-semibold">Jurisprudência (opcional)</h3>
        <p className="text-sm text-gray-600">Envie decisões, acórdãos ou trechos jurisprudenciais que possam ser úteis.</p>

        <div className="mt-3">
          <ComponenteUploadDocumentos
            permitirMultiplosArquivos={true}
            categoriaDocumento="jurisprudencia"
            processoId={processoId}
            aoFinalizarUploadComSucesso={(ids) => {
              console.log('[ComponenteUploadSindicaisJurisprudencia] Jurisprudência enviada:', ids);
              setIdsJurisprudencia((prev) => {
                const combinado = [...prev, ...ids];
                aoFinalizarUploads?.({ convencoes_sindicais: idsConvencoes, jurisprudencia: combinado });
                return combinado;
              });
            }}
            aoOcorrerErroNoUpload={(mensagemErro) => {
              console.error('[ComponenteUploadSindicaisJurisprudencia] Erro (jurisprudencia):', mensagemErro);
              aoOcorrerErro?.(mensagemErro);
            }}
          />
        </div>
      </div>
    </div>
  );
}
